import React, { useState, useEffect } from "react";
import {
  Search,
  Clock,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  MessageSquare,
  AlertTriangle,
  Building2,
  Package,
  Shield,
  X,
} from "lucide-react";
import heartLoading from "../../../assests/heartLoading.gif";
import axios from "axios";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";

const BASE_URL = process.env.REACT_APP_API_URL;

const DetailModal = ({ term, asset, loading, onClose }) => {
  if (!term) return null;

  const displayed = loading ? null : asset || term;

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const day = String(d.getUTCDate()).padStart(2, "0");
    const month = d.toLocaleString("en-GB", {
      month: "short",
      timeZone: "UTC",
    });
    const year = String(d.getUTCFullYear()).slice(-2);
    return `${day} ${month} '${year}`;
  };

  const NoData = () => <span className="text-gray-400 text-xs italic">—</span>;

  const RightField = ({ label, children }) => (
    <div className="mb-4">
      <p className="text-xs font-bold text-[#bb1ccc] mb-1">{label}</p>
      <div className="text-sm">{children}</div>
    </div>
  );

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 z-[9999] flex items-center justify-center bg-black/40">
      <div className="w-[50%] min-h-[500px] max-h-[90vh] rounded-lg bg-white shadow-xl overflow-y-auto">
        <div className="flex h-full">
          {/* LEFT PANEL */}
          <div className="flex-1 border-r p-4 min-h-[500px] flex flex-col">
            <div className="flex items-start justify-between mb-1">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-semibold text-[#5a58ec]">
                    {term.displayName}
                  </h2>
                  {term.status && (
                    <span className="text-[11px] text-[#EDE9FE] bg-[#bb1ccc] px-2 py-0.5 rounded-full font-medium">
                      {term.status}
                    </span>
                  )}
                  {term.cl3Community && (
                    <span className="text-[11px] text-[#CD0B7F] bg-[#F6EBF2] px-2 py-0.5 rounded-full font-medium">
                      {term.cl3Community}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center flex-1 py-16">
                <img
                  src={heartLoading}
                  alt="Loading..."
                  style={{ width : 200}}
                />
                <p className="text-gray-500 text-sm">Loading details...</p>
              </div>
            ) : (
              <>
                {/* Description */}
                <div className="mt-4">
                  <p className="text-sm font-medium mb-1">Description</p>
                  <div
                    className="text-sm text-black h-[80px] overflow-y-auto p-2 rounded bg-gray-50
                      [&::-webkit-scrollbar]:w-1
                      [&::-webkit-scrollbar-track]:bg-[#EDE9F3] [&::-webkit-scrollbar-track]:rounded-full
                      [&::-webkit-scrollbar-thumb]:bg-[#7C3AED] [&::-webkit-scrollbar-thumb]:rounded-full"
                    style={{
                      scrollbarWidth: "thin",
                      scrollbarColor: "#7C3AED #EDE9F3",
                    }}
                  >
                    {displayed?.description ? (
                      displayed.description
                    ) : (
                      <span className="text-gray-400 italic text-xs">
                        No description available
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <div className="mt-2 rounded bg-[#EDE9F3] p-3">
                    <p className="text-sm font-medium text-black mb-1">
                      Formula
                    </p>
                    <span className="text-gray-400 text-xs italic">
                      We don't have any details to show you
                    </span>
                  </div>
                </div>

                {displayed?.subDomain && (
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-1">Sub Domain</p>
                    <p className="text-sm text-gray-700">
                      {displayed.subDomain}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* RIGHT PANEL */}
          <div className="relative w-80 bg-[#F0FDFA] p-4">
            <X
              onClick={onClose}
              className="absolute right-2 top-3 h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700"
            />

            {loading ? null : (
              <>
                <RightField label="CDE">
                  <span
                    style={{
                      color:
                        displayed?.isCDE === "yes"
                          ? "#15803D"
                          : displayed?.isCDE === "no"
                            ? "#D17A00"
                            : "#6B7280",
                    }}
                  >
                    {displayed?.isCDE === "yes" ? (
                      "Yes"
                    ) : displayed?.isCDE === "no" ? (
                      "No"
                    ) : (
                      <NoData />
                    )}
                  </span>
                </RightField>

                <RightField label="DOMAIN">
                  {displayed?.cl3Community || <NoData />}
                </RightField>


                {displayed?.country && (
                  <RightField label="COUNTRY">{displayed.country}</RightField>
                )}

                <RightField label="SOURCE SYSTEM">
                  {displayed?.sourceSystem &&
                  displayed.sourceSystem !== "Not Available" ? (
                    displayed.sourceSystem
                  ) : (
                    <NoData />
                  )}
                </RightField>

                <RightField label="LAST UPDATED">
                  {formatDate(displayed?.date) || <NoData />}
                </RightField>

                <RightField label="CREATED AT">
                  {formatDate(displayed?.createdAt) || <NoData />}
                </RightField>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


const BusinessDescription = ({
  glossaryTerms = [],
  glossaryLoading = false,
  currentPage = 1,
  totalCount = 0,
  pageSize = 20,
  onPageChange,
  onSearch,
  onSortChange,
  onFilterChange,
  filterOptions = { domain: [], subDomain: [] },
}) => {
  const totalPages = Math.ceil(totalCount / pageSize);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [assetDetail, setAssetDetail] = useState(null);
  const [assetDetailLoading, setAssetDetailLoading] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(270);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    domain: true,
    subDomain: false,
    country: false,
  });
  const [selectedFilters, setSelectedFilters] = useState({
    domain: [],
    subDomain: [],
    country: [],
  });

  const levelColors = {
    domain: { text: "#16A34A", bg: "#DCFCE7", border: "#BBF7D0" },
    subDomain: { text: "#bb1ccc", bg: "#EFF6FF", border: "#BFDBFE" },
    country: { text: "#2563eb", bg: "#e0e7ff", border: "#bfdbfe" },
  };

  const groupLabels = {
    domain: "Domain",
    subDomain: "Sub Domain",
    country: "Country",
  };

  useEffect(() => {
    const timer = setTimeout(() => onSearch?.(searchTerm), 1000);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    onSortChange?.(sortBy, sortOrder);
  }, [sortBy, sortOrder]);

  useEffect(() => {
    onFilterChange?.(selectedFilters);
  }, [selectedFilters]);

  const handleMouseDown = (e) => {
    if (isCollapsed) return;
    e.preventDefault();
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    const startX = e.clientX;
    const startWidth = sidebarWidth;
    const onMove = (e) =>
      setSidebarWidth(
        Math.min(280, Math.max(150, startWidth + (e.clientX - startX))),
      );
    const onUp = () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const toggleFilter = (group, value) => {
    setSelectedFilters((prev) => {
      const list = prev[group] || [];
      return {
        ...prev,
        [group]: list.includes(value)
          ? list.filter((v) => v !== value)
          : [...list, value],
      };
    });
  };

  const resetFilters = () => setSelectedFilters({ domain: [], subDomain: [], country: [] });

  const fetchAssetDetail = async (id) => {
    try {
      setAssetDetailLoading(true);
      const res = await axios.get(
        `${BASE_URL}/business-description/asset/${id}`,
      );
      setAssetDetail(res.data);
    } catch (e) {
      console.error("Failed to fetch Business Description asset detail", e);
      setAssetDetail(null);
    } finally {
      setAssetDetailLoading(false);
    }
  };

  const renderFilterGroup = (groupKey) => {
    const values = filterOptions[groupKey] || [];
    const color = levelColors[groupKey];
    const label = groupLabels[groupKey];
    const isOpen = expandedGroups[groupKey];
    const checked = selectedFilters[groupKey] || [];
    if (!values.length) return null;

    return (
      <div key={groupKey} className="mb-1">
        <div
          className="flex items-center px-2 py-1.5 text-sm rounded-sm mx-1 cursor-pointer hover:bg-gray-100"
          onClick={() =>
            setExpandedGroups((p) => ({ ...p, [groupKey]: !p[groupKey] }))
          }
        >
          {isOpen ? (
            <ChevronDown
              size={13}
              className="mr-1 text-gray-400 flex-shrink-0"
            />
          ) : (
            <ChevronRight
              size={13}
              className="mr-1 text-gray-400 flex-shrink-0"
            />
          )}
          <span
            className="text-[12px]"
            style={{ color: color.text, fontWeight: 600 }}
          >
            {label}
          </span>
          {checked.length > 0 && (
            <span className="ml-auto bg-gray-100 px-1.5 py-0.5 rounded-xl text-[10px] text-gray-500 flex-shrink-0">
              {checked.length}
            </span>
          )}
        </div>
        {isOpen && (
          <div
            style={{
              marginLeft: "20px",
              borderLeft: "1.5px solid #e5e7eb",
              paddingLeft: "4px",
            }}
          >
            {values.map((val) => (
              <div
                key={val}
                className="flex items-center px-2 py-1.5 rounded-sm mx-1 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  className="mr-1.5 flex-shrink-0 accent-indigo-600 cursor-pointer"
                  checked={checked.includes(val)}
                  onChange={() => toggleFilter(groupKey, val)}
                />
                <span
                  className="truncate text-[12px]"
                  style={{
                    color: checked.includes(val) ? color.text : "#374151",
                    fontWeight: checked.includes(val) ? 600 : 400,
                  }}
                >
                  {val}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="flex h-[calc(100vh-200px)] bg-white">
        {/* ── Left Sidebar (same structure as GlossaryDiscovery) ── */}
        <div
          className="border-r border-gray-200 flex-shrink-0 relative flex flex-col"
          style={{
            width: isCollapsed ? "0px" : `${sidebarWidth}px`,
            overflow: "visible",
            transition: "width 0.22s cubic-bezier(.4,0,.2,1)",
            minWidth: isCollapsed ? "0px" : undefined,
          }}
        >
          <div
            style={{
              opacity: isCollapsed ? 0 : 1,
              transition: "opacity 0.15s",
              overflow: "hidden",
              width: `${sidebarWidth}px`,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="flex items-center justify-between px-1 pt-2 pb-2">
              <span className="text-[10px] font-semibold text-gray-500 tracking-wider uppercase">
                Filter Options
              </span>
            </div>

            <button
              onClick={resetFilters}
              className="flex items-center gap-1 mb-2 text-[10px] w-[60px] font-semibold px-2 py-1 rounded-md border border-gray-300 text-gray-500 hover:bg-red-50 hover:text-red-500 hover:border-red-300 transition-colors"
            >
              <RotateCcw size={10} />
              Reset
            </button>

            {/* Level legend badges */}
            <div className="flex gap-1.5 mb-3 flex-wrap px-2">
              {["domain", "subDomain", "country"].map((key) => (
                <span
                  key={key}
                  className="inline-flex items-center gap-1 py-[3px] px-2.5 text-[11px] font-normal rounded-[6px] tracking-[0.03em]"
                  style={{
                    backgroundColor: levelColors[key].bg,
                    color: levelColors[key].text,
                    border: `1.5px solid ${levelColors[key].border}`,
                  }}
                >
                  {key === "domain" && (
                    <Package size={11} color={levelColors[key].text} />
                  )}
                  {key === "subDomain" && (
                    <Shield size={11} color={levelColors[key].text} />
                  )}
                  {key === "country" && (
                    <span style={{ fontWeight: 700 }}>🌍</span>
                  )}
                  {groupLabels[key]}
                </span>
              ))}
            </div>

            {/* Filter tree */}
            <div className="flex-1 overflow-y-auto pb-4">
              {["domain", "subDomain", "country"].map(renderFilterGroup)}
            </div>
          </div>

          {/* Resize handle */}
          <div
            onMouseDown={handleMouseDown}
            onMouseEnter={(e) => {
              if (!isCollapsed)
                e.currentTarget.style.backgroundColor = "#bb1ccc";
            }}
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
            className={`absolute top-0 right-0 h-full w-[4px] transition-colors duration-150 z-10 ${isCollapsed ? "cursor-default" : "cursor-col-resize"}`}
          />

          {/* Collapse toggle — identical to GlossaryDiscovery */}
          <button
            onClick={() => setIsCollapsed((p) => !p)}
            className="absolute top-1/2 -translate-y-1/2 -right-[13px] w-[26px] h-[26px] rounded-full bg-[#bb1ccc] border-2 border-white flex items-center justify-center cursor-pointer z-30 shrink-0"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              className="transition-transform duration-[220ms]"
              style={{
                transform: isCollapsed ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              <path
                d="M7 2L3 6L7 10"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* ── Main Content ── */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header — same layout as GlossaryDiscovery */}
          <div className="flex flex-wrap mb-2 justify-between px-3">
            <div className="relative mr-[5px] flex items-center gap-2">
              <div className="relative w-[200px]">
                <Search
                  size={14}
                  className="absolute left-2.5 top-2.5 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search Terms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-1.5 pl-5 pr-8 border border-gray-300 rounded-md text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      onSearch?.("");
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-none border-none cursor-pointer"
                    type="button"
                  >
                    <X size={10} />
                  </button>
                )}
              </div>
            </div>

            <div className="flex gap-2 items-center">
              <p>Sort by: </p>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-xs font-semibold border border-gray-300 rounded-md px-2 py-1.5 h-[34px] outline-none focus:border-[#bb1ccc] cursor-pointer"
              >
                <option value="name">Asset Name</option>
                <option value="updatedAt">Last Updated</option>
              </select>

              <div className="flex h-[33px] border border-gray-300 rounded-md overflow-hidden">
                <button
                  onClick={() => setSortOrder("asc")}
                  className={`px-3 text-xs font-semibold transition-colors ${sortOrder === "asc" ? "bg-[#bb1ccc] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                >
                  ASC
                </button>
                <button
                  onClick={() => setSortOrder("desc")}
                  className={`px-3 text-xs font-semibold border-l border-gray-300 transition-colors ${sortOrder === "desc" ? "bg-[#bb1ccc] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                >
                  DESC
                </button>
              </div>
            </div>
          </div>

          {/* Count + Pagination — identical to GlossaryDiscovery */}
          <div className="px-3 pb-4 pt-1 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div className="mb-[10px]">
                <span className="bg-[#F0FDFA] text-[#14B8A6] text-sm px-3 py-1 rounded-md shadow-sm">
                  There are {totalCount} Unique Results Found
                </span>
              </div>
              {totalPages >= 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-2 py-2 rounded text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                  >
                    Prev
                  </button>
                  {(() => {
                    const pages = [];
                    if (totalPages <= 5) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else {
                      pages.push(1);
                      if (currentPage > 4) pages.push("...");
                      for (
                        let i = Math.max(2, currentPage - 2);
                        i <= Math.min(totalPages - 1, currentPage + 2);
                        i++
                      )
                        pages.push(i);
                      if (currentPage < totalPages - 3) pages.push("...");
                      pages.push(totalPages);
                    }
                    return pages.map((page, idx) =>
                      page === "..." ? (
                        <span
                          key={`dot-${idx}`}
                          className="px-3 py-2 text-gray-400 text-sm"
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => onPageChange(page)}
                          className="px-3 py-2 rounded text-sm font-medium border"
                          style={{
                            backgroundColor:
                              currentPage === page ? "#bb1ccc" : "#fff",
                            color: currentPage === page ? "#fff" : "#374151",
                            borderColor:
                              currentPage === page ? "#bb1ccc" : "#D1D5DB",
                          }}
                        >
                          {page}
                        </button>
                      ),
                    );
                  })()}
                  <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-2 py-2 rounded text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Card list — EXACT same JSX as GlossaryDiscovery ── */}
          <div className="flex-1 overflow-auto p-3 bg-[#f2f2f3]">
            {glossaryLoading ? (
              <div className="flex items-center justify-center py-12 mt-[3rem]">
                <div className="flex flex-col items-center justify-center space-y-3">
                  <img
                    src={heartLoading}
                    alt="Loading..."
                    style={{ width : 200}}
                  />
                  <p className="text-gray-600 text-[18px]">
                    Loading details...
                  </p>
                </div>
              </div>
            ) : glossaryTerms.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <h3 className="text-base font-semibold text-gray-600 mb-1">
                  No results found
                </h3>
                <p className="text-sm text-gray-400 max-w-[280px]">
                  No glossary terms match your search or filters. Try adjusting
                  your query.
                </p>
              </div>
            ) : (
              <div className="space-y-3" style={{ minWidth: "max-content" }}>
                {glossaryTerms.map((term) => (
                  <div
                    key={term.id}
                    className="bg-white rounded-lg border border-gray-200 p-3 cursor-pointer hover:shadow-md h-[90px] transition-shadow flex justify-between items-start"
                    style={{ minWidth: "100%" }}
                    onClick={() => {
                      setSelectedTerm(term);
                      fetchAssetDetail(term.id);
                    }}
                  >
                    {/* Left Side — same as GlossaryDiscovery */}
                    <div
                      className="pr-4 min-w-0"
                      style={{ flex: "1 1 0", overflow: "hidden" }}
                    >
                      <div
                        className="flex items-center gap-2 mb-3"
                        style={{ flexWrap: "nowrap", overflow: "hidden" }}
                      >
                          <Tooltip arrow title={term.displayName || ""}>
                            <h3
                              className="text-base font-bold text-[#5a58ec] truncate"
                              style={{ maxWidth: "250px", flexShrink: 1 }}
                            >
                              {term.displayName}
                            </h3>
                          </Tooltip>

                          {/* Country column */}
                          {term.country && (
                            <span
                              className="text-[11px] text-[#2563eb] bg-[#e0e7ff] px-2 py-0.5 rounded-full font-medium truncate"
                              style={{ maxWidth: "120px", flexShrink: 1 }}
                            >
                              {term.country}
                            </span>
                          )}

                        {/* status badge — only shown when value exists */}
                        {term.status && (
                          <span
                            className="text-[11px] text-[#EDE9FE] bg-[#bb1ccc] px-2 py-0.5 rounded-full font-medium flex items-center gap-1"
                            style={{ flexShrink: 0 }}
                          >
                            <Tooltip title={term.statusDescription || ""} arrow>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  cursor: "pointer",
                                }}
                              >
                                <InfoOutlineIcon
                                  sx={{ fontSize: "13px !important" }}
                                />
                              </span>
                            </Tooltip>
                            {term.status}
                          </span>
                        )}

                        {/* community badge (= domain) */}
                        {term.cl3Community ? (
                          <Tooltip arrow title={term.cl3Community}>
                            <span
                              className="text-[11px] text-[#CD0B7F] bg-[#F6EBF2] px-2 py-0.5 rounded-full font-medium truncate"
                              style={{ maxWidth: "160px", flexShrink: 1 }}
                            >
                              {term.cl3Community}
                            </span>
                          </Tooltip>
                        ) : (
                          <span
                            className="text-[11px] text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full font-medium"
                            style={{ flexShrink: 0 }}
                          >
                            No Domain
                          </span>
                        )}
                      </div>

                      <Tooltip title={term.description || ""} arrow>
                        <p
                          className={`text-sm leading-relaxed truncate ${!term.description ? "text-gray-400 italic" : "text-black"}`}
                        >
                          {term.description?.length > 50
                            ? term.description.slice(0, 50) + "..."
                            : term.description || "No description available"}
                        </p>
                      </Tooltip>
                    </div>

                    {/* Right Side — same as GlossaryDiscovery */}
                    <div
                      className="flex items-center gap-4 text-xs text-black"
                      style={{ flexShrink: 0, marginTop: "10px" }}
                    >
                      {/* CDE badge */}
                      <div
                        className="flex items-center"
                        style={{ width: "170px" }}
                      >
                        {term.isCDE === "yes" ? (
                          <span
                            className="text-[13px] truncate text-green-700 font-medium px-2.5 py-0.5 rounded-full border border-green-300 flex items-center gap-1.5"
                            style={{ backgroundColor: "#ddffd6" }}
                          >
                            <span
                              className="rounded-full bg-green-500 inline-block"
                              style={{ width: "6px", height: "6px" }}
                            />
                            Is CDE : Yes
                          </span>
                        ) : term.isCDE === "no" ? (
                          <span
                            className="text-[13px] truncate font-medium px-2.5 py-0.5 rounded-full border flex items-center gap-1.5"
                            style={{
                              color: "#D17A00",
                              backgroundColor: "#FFF3E0",
                              borderColor: "#f5c47a",
                            }}
                          >
                            <span
                              className="rounded-full inline-block"
                              style={{
                                width: "6px",
                                height: "6px",
                                backgroundColor: "#D17A00",
                              }}
                            />
                            Is CDE : No
                          </span>
                        ) : (
                          <span
                            className="text-[13px] truncate text-gray-500 font-medium px-2.5 py-0.5 rounded-full border border-gray-300 flex items-center gap-1.5"
                            style={{ backgroundColor: "#F3F4F6" }}
                          >
                            <span
                              className="rounded-full bg-gray-400 inline-block"
                              style={{ width: "6px", height: "6px" }}
                            />
                            Is CDE : N/A
                          </span>
                        )}
                      </div>

                      {/* Date (= updatedAt) */}
                      <div
                        className="flex items-center gap-1.5"
                        style={{ width: "140px" }}
                      >
                        <Clock
                          size={14}
                          className="text-[#14B8A6]"
                          style={{ flexShrink: 0 }}
                        />
                        <span className="text-[13px] whitespace-nowrap">
                          {term?.date
                            ? (() => {
                                const d = new Date(term.date);
                                const day = String(d.getUTCDate()).padStart(
                                  2,
                                  "0",
                                );
                                const month = d.toLocaleString("en-GB", {
                                  month: "short",
                                  timeZone: "UTC",
                                });
                                const year = String(d.getUTCFullYear()).slice(
                                  -2,
                                );
                                return `${day} ${month} '${year}`;
                              })()
                            : ""}
                        </span>
                      </div>

                      {/* Feedback slot — always empty for Business Description, space reserved */}
                      <div className="flex items-center gap-2 w-[70px]" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail modal */}
      {selectedTerm && (
        <DetailModal
          term={selectedTerm}
          asset={assetDetail}
          loading={assetDetailLoading}
          onClose={() => {
            setSelectedTerm(null);
            setAssetDetail(null);
          }}
        />
      )}
    </>
  );
};

export default BusinessDescription;
